import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #splitResultsLoaded = false;
  #isHydratingSplitResults = false;

  shouldInitializePagination() {
    return !this.#shouldSplitResults();
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#shouldSplitResults()) {
      requestIdleCallback(() => {
        this.#hydrateSplitResults();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.#isHydratingSplitResults) return;

    this.pages.clear();
    this.#splitResultsLoaded = false;

    if (this.#shouldSplitResults()) {
      requestIdleCallback(() => {
        this.#hydrateSplitResults();
      });
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { articleGrid, grid } = this.refs;
    articleGrid?.setAttribute('product-grid-view', value);
    grid?.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  buildPageURL(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    if (this.#shouldSplitResults()) {
      url.searchParams.set('type', 'product');
    }
    url.hash = '';
    return url;
  }

  buildHistoryURL(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    if (this.#shouldSplitResults()) {
      url.searchParams.set('type', 'product,article');
    }
    url.hash = '';
    return url;
  }

  #shouldSplitResults() {
    return this.getAttribute('results-mode') == 'split-by-type' && this.#getSearchTerm() !== '';
  }

  #getSearchTerm() {
    return new URL(window.location.href).searchParams.get('q')?.trim() || '';
  }

  async #hydrateSplitResults() {
    if (this.#splitResultsLoaded || !this.isConnected) return;
    this.#splitResultsLoaded = true;
    this.#isHydratingSplitResults = true;

    try {
      await this.#replaceWithProductResults();

      if (!this.#hasActiveFilters()) {
        await this.#appendAllArticleResults();
      } else {
        this.#removeArticlesGroup();
        super.updatedCallback();
      }

      this.initializePagination();
    } finally {
      this.#isHydratingSplitResults = false;
    }
  }

  async #replaceWithProductResults() {
    const productMarkup = await sectionRenderer.getSectionHTML(this.sectionId, true, this.buildPageURL(this.#getCurrentPage()));
    const parsedDocument = new DOMParser().parseFromString(productMarkup, 'text/html');
    const fetchedResultsList = parsedDocument.querySelector('results-list');

    if (!(fetchedResultsList instanceof HTMLElement)) return;

    this.innerHTML = fetchedResultsList.innerHTML;
    super.updatedCallback();
    this.pages.clear();
    this.#applyStoredLayout();
  }

  async #appendAllArticleResults() {
    const articleUrl = this.#buildArticleURL(1);
    const articleMarkup = await sectionRenderer.getSectionHTML(this.sectionId, true, articleUrl);
    const parsedDocument = new DOMParser().parseFromString(articleMarkup, 'text/html');
    const articleGroup = parsedDocument.querySelector('[data-results-group="articles"]');
    const articleGrid = articleGroup?.querySelector('[ref="articleGrid"]');
    const articlePageTracker = parsedDocument.querySelector('[ref="grid"]');

    if (!(articleGroup instanceof HTMLElement) || !(articleGrid instanceof HTMLElement) || !articlePageTracker) {
      return;
    }

    const lastPage = Number(articlePageTracker.getAttribute('data-last-page') || '1');

    for (let page = 2; page <= lastPage; page++) {
      const pagedMarkup = await sectionRenderer.getSectionHTML(this.sectionId, true, this.#buildArticleURL(page));
      const pagedDocument = new DOMParser().parseFromString(pagedMarkup, 'text/html');
      const pagedItems = pagedDocument.querySelectorAll('[ref="articleGrid"] > [ref="articleCards[]"]');
      articleGrid.append(...pagedItems);
    }

    if (articleGrid.children.length === 0) {
      return;
    }

    this.#removeArticlesGroup();
    articleGroup.removeAttribute('hidden');
    this.querySelector('#ResultsList > div')?.append(articleGroup);
    super.updatedCallback();
    this.#applyStoredLayout();
  }

  #buildArticleURL(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.searchParams.set('type', 'article');
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
      }
    }
    url.hash = '';
    return url;
  }

  #getCurrentPage() {
    return Number(new URL(window.location.href).searchParams.get('page') || '1');
  }

  #applyStoredLayout() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    this.#setLayout(storedLayout);
  }

  #hasActiveFilters() {
    return [...new URL(window.location.href).searchParams.keys()].some((key) => key.startsWith('filter.'));
  }

  #removeArticlesGroup() {
    this.querySelector('[data-results-group="articles"]')?.remove();
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
